import { useEffect } from 'react';
import { siteConfig } from '@/config/site';

interface SeoProps {
  title: string;
  description?: string;
  keywords?: string;
  path?: string;
  jsonLd?: object[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function Seo({ title, description, keywords, path = '', jsonLd = [] }: SeoProps) {
  const fullTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name;
  const fullDesc = description || siteConfig.description;
  const fullKeywords = keywords || siteConfig.keywords;
  const canonical = `${siteConfig.url}${path}`;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta('name', 'description', fullDesc);
    upsertMeta('name', 'keywords', fullKeywords);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', fullDesc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', siteConfig.name);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', fullDesc);

    let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    // 注入 JSON-LD 结构化数据
    const scripts = document.querySelectorAll('script[data-jsonld]');
    scripts.forEach((s) => s.remove());
    jsonLd.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-jsonld', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }, [fullTitle, fullDesc, fullKeywords, canonical, jsonLd]);

  return null;
}

export default Seo;

export const baseOrgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteConfig.name,
  url: siteConfig.url,
  logo: `${siteConfig.url}/logo.png`,
  description: siteConfig.description,
  email: siteConfig.email,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: siteConfig.phone,
    contactType: 'customer service',
    areaServed: 'CN',
    availableLanguage: 'Chinese',
  },
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
  inLanguage: 'zh-CN',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteConfig.url}/category?keyword={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};